import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ChevronLeft,
  Filter,
  Search,
  CheckCircle2,
  AlertCircle,
  Timer,
  XCircle,
  Eye,
  CreditCard,
  TrendingUp,
  Users,
  DollarSign,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import Footer from "../../components/landing/Footer";

const STATUS_CONFIG = {
  pending: {
    label: "Payment Pending",
    icon: Timer,
    color: "amber",
    bgClass: "bg-amber-50",
    borderClass: "border-amber-300",
    iconClass: "text-amber-600",
    gradientClass: "from-amber-400 to-orange-500",
  },
  approved: {
    label: "Paid & Confirmed",
    icon: CheckCircle2,
    color: "green",
    bgClass: "bg-green-50",
    textClass: "text-green-700",
    borderClass: "border-green-300",
    iconClass: "text-green-600",
    gradientClass: "from-green-400 to-emerald-500",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    color: "red",
    bgClass: "bg-red-50",
    textClass: "text-red-700",
    borderClass: "border-red-300",
    iconClass: "text-red-600",
    gradientClass: "from-red-400 to-pink-500",
  },
  under_review: {
    label: "Under Review",
    icon: AlertCircle,
    color: "blue",
    bgClass: "bg-blue-50",
    textClass: "text-blue-700",
    borderClass: "border-blue-300",
    iconClass: "text-blue-600",
    gradientClass: "from-blue-400 to-indigo-500",
  },
};

export default function ConferenceRegistrations() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalSpent: 0,
  });

  const calculateStats = useCallback((events) => {
    const conferenceEvents = events.filter((e) => 
      e.event?.event_type?.includes("conference")
    );
    
    const stats = {
      total: conferenceEvents.length,
      pending: conferenceEvents.filter((e) => e.status === "pending" || !e.status).length,
      approved: conferenceEvents.filter((e) => e.status === "approved").length,
      rejected: conferenceEvents.filter((e) => e.status === "rejected").length,
      totalSpent: conferenceEvents
        .filter((e) => e.status === "approved")
        .reduce((sum, e) => sum + (parseFloat(e.amount_paid) || 0), 0),
    };
    setStats(stats);
  }, []);

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await conferenceAndTradeMissionService.getAllRegisteredEvents("conference");

      if (response.status) {
        // Service already unwraps axios response to { status, data, message }
        // Backend shape for this endpoint is typically:
        // { status_code, message, reason, data: { "1": {..}, "2": {..} } }
        const apiPayload = response.data;
        const rawContainer = apiPayload?.data ?? apiPayload;
        const events = Array.isArray(rawContainer)
          ? rawContainer
          : rawContainer && typeof rawContainer === "object"
            ? Object.values(rawContainer)
            : [];
        setRegistrations(events);
        calculateStats(events);
      } else {
        toast.error(response.message || "Failed to fetch registrations");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.reason || "Failed to fetch registrations"
      );
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  const applyFilters = useCallback(() => {
    let filtered = [...registrations];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (reg) =>
          reg.event?.event_title?.toLowerCase().includes(q) ||
          reg.application_id?.toLowerCase().includes(q) ||
          reg.slug?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (reg) => (reg.status || "pending") === statusFilter
      );
    }

    setFilteredRegistrations(filtered);
  }, [registrations, searchQuery, statusFilter]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  // Single registrations often have registration_type: null; treat as "Single"
  const getRegistrationTypeLabel = (reg) =>
    reg.registration_type === "group" || reg.group_info ? "Group" : "Single";

  const handleViewDetails = (registration) => {
    const regId = registration.id || registration.slug || registration.event_slug;
    navigate(`/conference-registration/${regId}`, {
      state: { registration }
    });
  };

  const handleCompletePayment = (registration) => {
    const slug = registration.event?.slug;
    if (!slug) return;
    const eventData = registration.event || {};
    const isGroup = registration.registration_type === "group" || !!registration.group_info;

    // Group registration: get counts and amounts from group_info / registration payload
    const numberOfAttendees =
      (isGroup && (registration.group_info?.number_of_attendees ?? registration.number_of_attendees)) ||
      (Array.isArray(registration.attendees) && registration.attendees.length) ||
      undefined;
    const ticketPrice = registration.ticket_type != null
      ? parseFloat(registration.ticket_type.price)
      : NaN;
    const computedTotal =
      numberOfAttendees != null && !Number.isNaN(ticketPrice)
        ? Number((numberOfAttendees * ticketPrice).toFixed(2))
        : undefined;
    const totalAmount =
      registration.total_amount != null
        ? parseFloat(registration.total_amount)
        : registration.amount != null
          ? parseFloat(registration.amount)
          : computedTotal;

    // Resolve selectedTicket from event.ticket_types to match payment page shape
    const ticketTypeName = registration.ticket_type?.name;
    const ticketTypePrice = registration.ticket_type != null ? parseFloat(registration.ticket_type.price) : NaN;
    const selectedTicket =
      eventData?.ticket_types?.length && (ticketTypeName != null || !Number.isNaN(ticketTypePrice))
        ? eventData.ticket_types.find((t) => t.name === ticketTypeName) ??
          eventData.ticket_types.find((t) => Math.abs(parseFloat(t.price) - ticketTypePrice) < 0.02) ??
          eventData.ticket_types[0]
        : registration.ticket_type ?? undefined;

    navigate(`/event-details/${slug}/payment`, {
      state: {
        registrationData: {
          ...registration,
          id: registration.id,
          registration_id: registration.id,
          slug: registration.slug || registration.application_id || registration.id,
        },
        eventData,
        slug,
        payLaterEntry: true,
        fromRegistrations: true,
        isGroup,
        numberOfAttendees,
        totalAmount,
        attendees: registration.attendees ?? undefined,
        selectedTicket,
      },
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header – glass style, distinct from registration flow */}
      <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-violet-500/[0.08] via-indigo-500/[0.06] to-transparent" aria-hidden />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-600">
              <Users size={20} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="mb-0.5 flex items-center justify-start">
                <span className="inline-flex items-center rounded-full border border-violet-200/70 bg-violet-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                  My registrations
                </span>
              </div>
              <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
                Conference Registrations
              </h1>
              <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm">
                Manage your conference bookings and payments
              </p>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-violet-500/10 hover:text-violet-700 sm:flex-none"
            >
              <ChevronLeft size={20} strokeWidth={2} />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back</span>
            </motion.button>
            <motion.button
              type="button"
              onClick={() => navigate("/conference-trade-mission")}
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.98 }}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-semibold text-violet-700 shadow-sm transition-colors hover:bg-violet-500/10 hover:text-violet-800 sm:flex-none"
            >
              <Sparkles size={18} />
              <span className="sm:hidden">Browse</span>
              <span className="hidden sm:inline">Browse Events</span>
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </header>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="grid grid-cols-2 gap-2 mb-6 md:gap-4 md:mb-8 md:grid-cols-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative overflow-hidden p-2.5 md:p-4 lg:p-6 bg-gradient-to-br from-purple-50 to-white rounded-lg md:rounded-xl lg:rounded-2xl border md:border-2 border-purple-200 shadow-md md:shadow-lg hover:shadow-lg md:hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-12 h-12 md:w-16 md:h-16 lg:w-24 lg:h-24 bg-purple-200/20 rounded-full blur-xl md:blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-1 md:mb-2 lg:mb-3">
                  <p className="text-[9px] md:text-[10px] lg:text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Total
                  </p>
                  <div className="p-1 md:p-1.5 lg:p-2 bg-purple-100 rounded md:rounded-lg group-hover:scale-110 transition-transform">
                    <TrendingUp size={12} className="md:w-4 md:h-4 lg:w-[18px] lg:h-[18px] text-purple-600" />
                  </div>
                </div>
                <p className="text-base md:text-xl lg:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-purple-800">
                  {stats.total}
                </p>
                <p className="mt-0.5 md:mt-1 text-[9px] md:text-[10px] lg:text-xs text-purple-600">Events registered</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 }}
              className="relative overflow-hidden p-2.5 md:p-4 lg:p-6 bg-gradient-to-br from-amber-50 to-white rounded-lg md:rounded-xl lg:rounded-2xl border md:border-2 border-amber-200 shadow-md md:shadow-lg hover:shadow-lg md:hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-12 h-12 md:w-16 md:h-16 lg:w-24 lg:h-24 bg-amber-200/20 rounded-full blur-xl md:blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-1 md:mb-2 lg:mb-3">
                  <p className="text-[9px] md:text-[10px] lg:text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Pending
                  </p>
                  <div className="p-1 md:p-1.5 lg:p-2 bg-amber-100 rounded md:rounded-lg group-hover:scale-110 transition-transform">
                    <Timer size={12} className="md:w-4 md:h-4 lg:w-[18px] lg:h-[18px] text-amber-600" />
                  </div>
                </div>
                <p className="text-lg md:text-2xl lg:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-800">
                  {stats.pending}
                </p>
                <p className="mt-0.5 md:mt-1 text-[9px] md:text-[10px] lg:text-xs text-amber-600">Awaiting payment</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative overflow-hidden p-2.5 md:p-4 lg:p-6 bg-gradient-to-br from-green-50 to-white rounded-lg md:rounded-xl lg:rounded-2xl border md:border-2 border-green-200 shadow-md md:shadow-lg hover:shadow-lg md:hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-12 h-12 md:w-16 md:h-16 lg:w-24 lg:h-24 bg-green-200/20 rounded-full blur-xl md:blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-1 md:mb-2 lg:mb-3">
                  <p className="text-[9px] md:text-[10px] lg:text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Confirmed
                  </p>
                  <div className="p-1 md:p-1.5 lg:p-2 bg-green-100 rounded md:rounded-lg group-hover:scale-110 transition-transform">
                    <CheckCircle2 size={12} className="md:w-4 md:h-4 lg:w-[18px] lg:h-[18px] text-green-600" />
                  </div>
                </div>
                <p className="text-lg md:text-2xl lg:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-green-800">
                  {stats.approved}
                </p>
                <p className="mt-0.5 md:mt-1 text-[9px] md:text-[10px] lg:text-xs text-green-600">Paid & confirmed</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.35 }}
              className="relative overflow-hidden p-2.5 md:p-4 lg:p-6 bg-gradient-to-br from-blue-50 to-white rounded-lg md:rounded-xl lg:rounded-2xl border md:border-2 border-blue-200 shadow-md md:shadow-lg hover:shadow-lg md:hover:shadow-xl transition-all group"
            >
              <div className="absolute top-0 right-0 w-12 h-12 md:w-16 md:h-16 lg:w-24 lg:h-24 bg-blue-200/20 rounded-full blur-xl md:blur-2xl" />
              <div className="relative">
                <div className="flex justify-between items-center mb-1 md:mb-2 lg:mb-3">
                  <p className="text-[9px] md:text-[10px] lg:text-xs font-bold tracking-wider text-gray-600 uppercase">
                    Total Spent
                  </p>
                  <div className="p-1 md:p-1.5 lg:p-2 bg-blue-100 rounded md:rounded-lg group-hover:scale-110 transition-transform">
                    <DollarSign size={12} className="md:w-4 md:h-4 lg:w-[18px] lg:h-[18px] text-blue-600" />
                  </div>
                </div>
                <p className="text-lg md:text-2xl lg:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
                  GH₵{stats.totalSpent.toFixed(2)}
                </p>
                <p className="mt-0.5 md:mt-1 text-[9px] md:text-[10px] lg:text-xs text-blue-600">Across all events</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="p-6 mb-8 bg-white rounded-2xl border border-gray-200 shadow-lg"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search
                  className="absolute top-3.5 left-4 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by event name or application ID..."
                  className="py-3 pr-4 pl-12 w-full text-sm text-gray-900 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 focus:bg-white"
                />
              </div>

              <div className="flex gap-2 items-center">
                <Filter size={20} className="text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-5 py-3 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all outline-none hover:bg-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Payment Pending</option>
                  <option value="approved">Paid & Confirmed</option>
                  <option value="rejected">Rejected</option>
                  <option value="under_review">Under Review</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Registrations List */}
          {loading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
                <div className="flex absolute inset-0 justify-center items-center">
                  <Users size={32} className="text-orange-500" />
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-600">Loading your registrations...</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-20 text-center"
            >
              <div className="flex justify-center mb-6">
                <div className="flex justify-center items-center w-24 h-24 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full">
                  <Users size={48} className="text-orange-600" />
                </div>
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-900">
                {searchQuery || statusFilter !== "all"
                  ? "No results found"
                  : "No conference registrations yet"}
              </h3>
              <p className="mb-8 text-base text-gray-600 max-w-md mx-auto">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your filters or search query"
                  : "Start by registering for a conference event"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <motion.button
                  onClick={() => navigate("/conference-trade-mission")}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex gap-2 items-center px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-xl transition-all hover:from-orange-600 hover:to-orange-700 hover:shadow-2xl"
                >
                  <Sparkles size={20} />
                  Browse Conference Events
                  <ArrowRight size={18} />
                </motion.button>
              )}
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid grid-cols-2 gap-2 md:gap-4 md:grid-cols-2 lg:gap-6 lg:grid-cols-4">
                {filteredRegistrations.map((registration, index) => {
                  const status = registration.status || "pending";
                  const statusConfig = getStatusConfig(status);
                  const StatusIcon = statusConfig.icon;
                  const eventData = registration.event || {};
                  const isPending = status === "pending" || !status;
                  const registrationTypeLabel = getRegistrationTypeLabel(registration);

                  return (
                    <motion.div
                      key={registration.id || index}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex overflow-hidden flex-col bg-white rounded-lg md:rounded-xl lg:rounded-2xl border border-gray-200 md:border-2 shadow-md md:shadow-lg transition-all hover:border-orange-300 hover:shadow-xl md:hover:shadow-2xl group"
                    >
                      {/* Event Image */}
                      <div className="relative h-28 sm:h-32 md:h-40 lg:h-48 bg-gradient-to-br from-orange-100 via-purple-100 to-blue-100">
                        {eventData.thumbnail && (
                          <img
                            src={eventData.thumbnail}
                            alt={eventData.event_title}
                            className="object-cover w-full h-full transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/50" />

                        {/* Status Badge */}
                        <div className="absolute top-1 right-1 md:top-2 md:right-2 lg:top-3 lg:right-3">
                          <div
                            className={`flex gap-1 md:gap-1.5 lg:gap-2 items-center px-1.5 py-0.5 md:px-2 md:py-1 lg:px-3 lg:py-1.5 text-[9px] md:text-[10px] lg:text-xs font-bold backdrop-blur-md rounded-full border md:border-2 ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass} shadow-md md:shadow-lg`}
                          >
                            <StatusIcon size={10} className="md:w-3 md:h-3 lg:w-3.5 lg:h-3.5" />
                            <span className="hidden sm:inline">{statusConfig.label}</span>
                            <span className="sm:hidden">{statusConfig.label.split(' ')[0]}</span>
                          </div>
                        </div>

                        {/* Conference & Registration type badges */}
                        <div className="absolute bottom-1 left-1 md:bottom-2 md:left-2 lg:bottom-3 lg:left-3 flex flex-wrap gap-1 md:gap-1.5">
                          <span className="inline-flex px-1.5 py-0.5 md:px-2 md:py-1 lg:px-3 lg:py-1.5 text-[9px] md:text-[10px] lg:text-xs font-bold text-white bg-blue-500/90 backdrop-blur-sm rounded-full border md:border-2 border-white/30 shadow-md md:shadow-lg">
                            <span className="hidden sm:inline">Conference</span>
                            <span className="sm:hidden">Conf</span>
                          </span>
                          <span className={`inline-flex px-1.5 py-0.5 md:px-2 md:py-1 lg:px-3 lg:py-1.5 text-[9px] md:text-[10px] lg:text-xs font-bold text-white backdrop-blur-sm rounded-full border md:border-2 border-white/30 shadow-md md:shadow-lg ${registrationTypeLabel === "Group" ? "bg-violet-500/90" : "bg-slate-600/90"}`}>
                            {registrationTypeLabel}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex flex-col flex-1 p-2 md:p-4 lg:p-6">
                        <h3 className="mb-1.5 md:mb-2 lg:mb-3 text-xs md:text-sm lg:text-lg font-bold leading-tight text-gray-900 transition-colors line-clamp-2 group-hover:text-orange-600">
                          {eventData.event_title || "Untitled Event"}
                        </h3>

                        <div className="flex-1 mb-2 md:mb-3 lg:mb-4 space-y-1.5 md:space-y-2 lg:space-y-3">
                          {/* Date & Time */}
                          {((eventData.event_start_date || eventData.event_date) || (eventData.event_end_date || eventData.event_date)) && (
                            <div className="flex flex-wrap gap-1 md:gap-1.5 lg:gap-2 items-center text-[10px] md:text-xs lg:text-sm text-gray-700">
                              <div className="flex gap-1 md:gap-1.5 lg:gap-2 items-center">
                                <div className="p-0.5 md:p-1 lg:p-1.5 bg-emerald-100 rounded md:rounded-lg">
                                  <Calendar size={10} className="md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 text-emerald-600" />
                                </div>
                                <span className="font-medium">
                                  {(() => {
                                    const start = eventData.event_start_date || eventData.event_date;
                                    const end = eventData.event_end_date || eventData.event_date;
                                    return start && end && start !== end
                                      ? `${formatDate(start)} – ${formatDate(end)}`
                                      : formatDate(start || eventData.event_date);
                                  })()}
                                </span>
                              </div>
                              {eventData.event_time && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <div className="flex gap-1 md:gap-1.5 lg:gap-2 items-center">
                                    <div className="p-0.5 md:p-1 lg:p-1.5 bg-emerald-100 rounded md:rounded-lg">
                                      <Clock size={10} className="md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 text-emerald-600" />
                                    </div>
                                    <span className="font-medium">
                                      {formatTime(eventData.event_time)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          {/* Venue */}
                          {eventData.venue_name && (
                            <div className="flex gap-1 md:gap-1.5 lg:gap-2 items-start">
                              <div className="p-0.5 md:p-1 lg:p-1.5 bg-blue-100 rounded md:rounded-lg flex-shrink-0">
                                <MapPin size={10} className="md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 text-blue-600" />
                              </div>
                              <span className="text-[10px] md:text-xs lg:text-sm text-gray-700 font-medium line-clamp-2">
                                {eventData.venue_name}
                              </span>
                            </div>
                          )}

                          {/* (Intentionally no extra registration type or application ID here – kept minimal on content side) */}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-1.5 md:gap-2 lg:gap-3">
                          <motion.button
                            onClick={() => handleViewDetails(registration)}
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex flex-1 gap-1 md:gap-1.5 lg:gap-2 justify-center items-center px-1.5 py-1 md:px-2.5 md:py-1.5 lg:px-4 lg:py-2.5 text-[10px] md:text-xs lg:text-sm font-bold text-gray-700 bg-gray-100 rounded-lg md:rounded-xl border md:border-2 border-gray-200 transition-all hover:bg-gray-200 hover:border-gray-300"
                          >
                            <Eye size={12} className="md:w-3.5 md:h-3.5 lg:w-4 lg:h-4" />
                            <span className="hidden sm:inline">View</span>
                          </motion.button>

                          {isPending && (
                            <motion.button
                              onClick={() => handleCompletePayment(registration)}
                              whileHover={{ scale: 1.02, y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              className="flex flex-1 gap-1 md:gap-1.5 lg:gap-2 justify-center items-center px-1.5 py-1 md:px-2.5 md:py-1.5 lg:px-4 lg:py-2.5 text-[10px] md:text-xs lg:text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg md:rounded-xl border border-orange-400 shadow-md transition-all hover:from-orange-600 hover:to-orange-700 hover:shadow-lg"
                            >
                              <CreditCard size={12} className="md:w-3.5 md:h-3.5 lg:w-4 lg:h-4" />
                              <span className="hidden sm:inline">Pay Now</span>
                              <span className="sm:hidden">Pay</span>
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

